import { useRef, useEffect, useId, useMemo, useState, useCallback } from "react";
import PropTypes from "prop-types";
import Loader from "../Loader";
import NoData from "../Nodata";
import { transformPowerOfferingData } from "../../utils/utility";
import { CHART_TITLE, SERIES_CONFIG, Y_AXIS_PADDING_MULTIPLIER } from "./constants";
import renderChart from "./renderChart";
import useClickOutside from "./hooks/useClickOutside";
import useResizeObserver from "./hooks/useResizeObserver";
import useChartExport from "./hooks/useChartExport";
import ChartContextMenu from "./components/ChartContextMenu";
import ChartLegend from "./components/ChartLegend";
import ChartTooltip from "./components/ChartTooltip";
import useIsDarkTheme from "../../hooks/useIsDarkTheme";
import "./LineBarComposedChart.scss";

const LineBarComposedChart = ({
  data = {},
  loading,
  powerConsumedData = [],
  events = [],
  dateRange = null,
  onZoomChange,
  zoomExtremes,
  siteTimezone,
}) => {
  const isDark = useIsDarkTheme();
  const chartData = data;
  const chartPowerConsumedData = powerConsumedData;
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const tooltipRef = useRef(null);
  const isSyncingRef = useRef(false);
  const menuRef = useRef(null);
  const panCleanupRef = useRef(null);
  const clipPathId = useId();
  const doRenderChartRef = useRef(null);

  const [visibility, setVisibility] = useState(() => {
    const map = {};
    SERIES_CONFIG.forEach(s => (map[s.name] = true));
    // Initialize events visibility
    if (events && events.length > 0) {
      map["events"] = true;
    }
    return map;
  });
  const [menuOpen, setMenuOpen] = useState(false);

  // Update events visibility when events are loaded
  useEffect(() => {
    setVisibility(prev => {
      const updated = { ...prev };
      if (events && events.length > 0) {
        updated["events"] = true;
      }
      return updated;
    });
  }, [events]);

  // Prepare all series data in a unified structure keyed by SERIES_CONFIG.key
  const allSeriesData = useMemo(() => {
    const rawPowerOffered = transformPowerOfferingData(chartData?.powerOfferingData || []);

    // Build plugged-in time ranges from EV Plugged In / EV Plugged Out events
    let maskedPowerOffered = rawPowerOffered;
    if (events && events.length > 0 && rawPowerOffered.length > 0) {
      const plugEvents = events
        .filter(e => e.eventName === "EV Plugged In" || e.eventName === "EV Plugged Out")
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

      if (plugEvents.length > 0) {
        const rangeStart = dateRange ? new Date(dateRange[0]).getTime() : 0;
        const ranges = [];
        // If first plug event is PO, EV was already plugged in before the date range
        let plugInTs = plugEvents[0].eventName === "EV Plugged Out" ? rangeStart : null;

        for (const evt of plugEvents) {
          if (evt.eventName === "EV Plugged In") {
            plugInTs = new Date(evt.date).getTime();
          } else if (evt.eventName === "EV Plugged Out") {
            if (plugInTs != null) {
              ranges.push([plugInTs, new Date(evt.date).getTime()]);
              plugInTs = null;
            }
          }
        }
        // If still plugged in (no matching PO), extend to Infinity
        if (plugInTs != null) {
          ranges.push([plugInTs, Infinity]);
        }

        if (ranges.length > 0) {
          // Insert null-value points at range boundaries to break the line at gaps
          const result = [];
          // Sort raw data by timestamp
          const sorted = [...rawPowerOffered].sort((a, b) => (a[0] || 0) - (b[0] || 0));

          for (let i = 0; i < sorted.length; i++) {
            const point = sorted[i];
            const ts = point[0];
            if (ts == null) {
              result.push(point);
              continue;
            }

            const inRange = ranges.some(([start, end]) => ts >= start && ts <= end);
            if (!inRange) {
              result.push([ts, null, point[2]]);
            } else {
              // Check if there's a gap between this point and the previous one
              if (i > 0 && result.length > 0) {
                const prevTs = sorted[i - 1][0];
                if (prevTs != null) {
                  const prevInRange = ranges.some(([start, end]) => prevTs >= start && prevTs <= end);
                  // If previous point was outside range (or in a different range), insert a null break
                  if (!prevInRange) {
                    result.push([ts - 1, null, point[2]]);
                  } else {
                    // Check if they're in different ranges (gap between sessions)
                    const prevRangeIdx = ranges.findIndex(([s, e]) => prevTs >= s && prevTs <= e);
                    const currRangeIdx = ranges.findIndex(([s, e]) => ts >= s && ts <= e);
                    if (prevRangeIdx !== currRangeIdx) {
                      // Insert null break between ranges
                      result.push([prevTs + 1, null, point[2]]);
                    }
                  }
                }
              }
              result.push(point);
            }
          }
          maskedPowerOffered = result;
        }
      }
    }

    return {
      greenChargingSetpointPower: chartData?.greenChargingSetpointPower || [],
      smartChargingSetpointPower: chartData?.smartChargingSetpointPower || [],
      userSetSchedulesSetpointPower: chartData?.userSetSchedulesSetpointPower || [],
      powerConsumedData: chartPowerConsumedData || [],
      powerOffered: maskedPowerOffered,
    };
  }, [chartData, chartPowerConsumedData, events, dateRange]);

  const hasData =
    (chartData?.greenChargingSetpointPower?.length ?? 0) > 0 ||
    (chartData?.smartChargingSetpointPower?.length ?? 0) > 0 ||
    (chartData?.userSetSchedulesSetpointPower?.length ?? 0) > 0 ||
    (chartPowerConsumedData?.length ?? 0) > 0 ||
    (events?.length ?? 0) > 0;

  // Compute full extent of all data
  const fullExtent = useMemo(() => {
    let minX = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    SERIES_CONFIG.forEach(cfg => {
      const d = allSeriesData[cfg.key];
      if (!d || !d.length) return;
      d.forEach(point => {
        const px = point[0];
        const py = point[1];
        if (px !== null && px !== undefined && px < minX) minX = px;
        if (px !== null && px !== undefined && px > maxX) maxX = px;
        if (py !== null && py !== undefined && py > maxY) maxY = py;
      });
    });
    if (minX === Infinity) return { minX: 0, maxX: 1, maxY: 1 };
    return { minX, maxX, maxY: maxY * Y_AXIS_PADDING_MULTIPLIER };
  }, [allSeriesData]);

  // Close menu on outside click
  const closeMenu = useCallback(() => setMenuOpen(false), []);
  useClickOutside(menuRef, closeMenu);

  // Export handlers via custom hook
  const exportActions = useChartExport(containerRef, svgRef, allSeriesData, closeMenu, siteTimezone);

  // Main D3 rendering — delegates to extracted pure function
  const doRenderChart = useCallback(() => {
    // Render if there's data OR if there are events to display
    const hasEvents = events && events.length > 0;
    if (!hasData && !hasEvents) return;
    // Clean up any dangling pan listeners from previous render
    if (panCleanupRef.current) {
      panCleanupRef.current();
      panCleanupRef.current = null;
    }
    renderChart({
      container: containerRef.current,
      svgEl: svgRef.current,
      tooltipEl: tooltipRef.current,
      allSeriesData,
      visibility,
      fullExtent,
      zoomExtremes,
      isSyncingRef,
      onZoomChange,
      clipPathId,
      panCleanupRef,
      isDark,
      siteTimezone,
      events,
      dateRange,
    });
  }, [
    allSeriesData,
    clipPathId,
    fullExtent,
    hasData,
    isDark,
    onZoomChange,
    visibility,
    zoomExtremes,
    siteTimezone,
    events,
    dateRange,
  ]);

  // Keep a stable ref to the latest doRenderChart for effects that
  // should NOT re-run when doRenderChart's identity changes.
  doRenderChartRef.current = doRenderChart;

  // Render on mount and data change
  useEffect(() => {
    doRenderChart();
  }, [doRenderChart]);

  // ResizeObserver for responsiveness
  useResizeObserver(containerRef, doRenderChart);

  // Sync incoming zoom (from sibling chart)
  useEffect(() => {
    if (!zoomExtremes || zoomExtremes.source === "line") return;
    isSyncingRef.current = true;
    doRenderChartRef.current?.();
    isSyncingRef.current = false;
  }, [zoomExtremes]);

  // Clean up dangling pan listeners on unmount
  useEffect(() => {
    return () => {
      if (panCleanupRef.current) {
        panCleanupRef.current();
        panCleanupRef.current = null;
      }
    };
  }, []);

  // Legend toggle handler
  const handleLegendToggle = useCallback(seriesName => {
    setVisibility(prev => ({
      ...prev,
      [seriesName]: !prev[seriesName],
    }));
  }, []);

  return (
    <div className="relative w-full rounded-xl shadow-lg transition-all duration-200 hover:shadow-xl line-bar-chart">
      <div className="line-bar-chart__title-bar">
        {CHART_TITLE}
        <ChartContextMenu
          open={menuOpen}
          onToggle={() => setMenuOpen(prev => !prev)}
          menuRef={menuRef}
          actions={exportActions}
        />
      </div>
      <div className="line-bar-chart__chart-area">
        {!loading && !hasData ? (
          <NoData />
        ) : loading ? (
          <div className="absolute inset-0 z-10 flex items-center justify-center line-bar-chart__loading-overlay">
            <Loader />
          </div>
        ) : (
          <div ref={containerRef} className="line-bar-chart__chart-container">
            <svg ref={svgRef} className="line-bar-chart__svg" />
            <ChartTooltip ref={tooltipRef} />
            <ChartLegend visibility={visibility} onToggle={handleLegendToggle} events={events} />
          </div>
        )}
      </div>
    </div>
  );
};

LineBarComposedChart.propTypes = {
  data: PropTypes.shape({
    greenChargingSetpointPower: PropTypes.array,
    smartChargingSetpointPower: PropTypes.array,
    userSetSchedulesSetpointPower: PropTypes.array,
    powerOfferingData: PropTypes.array,
  }),
  loading: PropTypes.bool.isRequired,
  powerConsumedData: PropTypes.array,
  events: PropTypes.array,
  dateRange: PropTypes.arrayOf(PropTypes.object),
  onZoomChange: PropTypes.func,
  zoomExtremes: PropTypes.shape({
    min: PropTypes.number.isRequired,
    max: PropTypes.number.isRequired,
    source: PropTypes.string.isRequired,
  }),
  siteTimezone: PropTypes.string,
};

LineBarComposedChart.defaultProps = {
  data: {},
  powerConsumedData: [],
  events: [],
  onZoomChange: null,
  zoomExtremes: null,
  siteTimezone: "UTC",
};

export default LineBarComposedChart;
